// hooks/useSession.ts - Fixed for library-based approach
import { useState, useRef } from 'react';
import { VideoState, SegmentState, normalizeVideoViewCrop } from '../types';
import { getFirstComboId, loadSessionFromFirebase, normalizeCombos, resolveSegmentOverlapSeconds } from '../utils';
import { stripShareSessionParams } from '../../utils/shareSessionUrl';
import { migrateLegacyTransitionType } from '../transitionCompat';

interface SessionState {
  showShareModal: boolean;
  shareUrl: string;
  isLoadingSession: boolean;
  sessionError: string | null;
}

export const useSession = () => {
  const [sessionState, setSessionState] = useState<SessionState>({
    showShareModal: false,
    shareUrl: '',
    isLoadingSession: false,
    sessionError: null
  });

  const sessionLoadAttemptedRef = useRef(false);

  /**
   * Load session from Firebase if session ID is in URL
   * This loads SHARED sessions (from old sessionService.js approach)
   */
  const handleLoadSessionFromUrl = async (
    setVideoState: (updates: Partial<VideoState>) => void,
    setSegmentState: (updates: Partial<SegmentState>) => void,
    setUIState: (updates: { isDemo: boolean }) => void,
    setOriginalVideoFile: (file: File | null) => void,
    providedSessionId?: string
  ) => {
    const sessionId = providedSessionId?.trim() || '';
    
    if (!sessionId) return;

    try {
      setSessionState(prev => ({ ...prev, isLoadingSession: true, sessionError: null }));
      
      const sessionData = await loadSessionFromFirebase(sessionId);
      
      // Convert string dates to Date objects
      const expiresAt = typeof sessionData.expiresAt === 'string' 
        ? new Date(sessionData.expiresAt) 
        : sessionData.expiresAt;
      
      console.log('🟢 Session loaded successfully:', sessionData.sessionId);
      
      // Set loading state for video
      setVideoState({ isLoading: true, loadingSource: 'firebase' });
      
      // Load the session data into your app
      const transitionMigration = migrateLegacyTransitionType(sessionData.settings.transitionType);
      const normalizedSegments = normalizeCombos(sessionData.segments);
      const loadedSegmentOverlapSeconds = resolveSegmentOverlapSeconds(sessionData.settings, normalizedSegments);

      setVideoState({
        videoSrc: sessionData.videoUrl,
        videoFileName: sessionData.filename,
        playbackRate: sessionData.settings.defaultSpeed || 1.0,
        isMirrored: sessionData.settings.defaultMirror || false,
        userMutePreference: sessionData.settings.defaultMuted || false,
        isPlaying: false,
        currentTime: 0,
        isLoading: true,
        viewCrop: normalizeVideoViewCrop(sessionData.settings.viewCrop)
      });
      
      setSegmentState({
        segments: normalizedSegments,
        activeSegment: null,
        activeComboId: getFirstComboId(normalizedSegments),
        isMarkingSegment: false,
        currentSegmentStart: null,
        isLooping: sessionData.settings.autoLoop || false,
        transitionType: transitionMigration.transitionType,
        segmentOverlap: loadedSegmentOverlapSeconds
      });
      
      setUIState({ isDemo: false });
      setOriginalVideoFile(null);
      
      // Clean up URL
      window.history.replaceState(
        {},
        document.title,
        stripShareSessionParams(window.location.href)
      );
      
      setSessionState(prev => ({ ...prev, isLoadingSession: false }));
      
      alert(`Session loaded! Expires on ${expiresAt.toLocaleDateString()}`);
      
    } catch (error) {
      console.error('Failed to load session:', error);
      setSessionState(prev => ({ 
        ...prev, 
        isLoadingSession: false, 
        sessionError: error instanceof Error ? error.message : 'Unknown error'
      }));
      window.history.replaceState(
        {},
        document.title,
        stripShareSessionParams(window.location.href)
      );

      const errorMessage = error instanceof Error ? error.message.toLowerCase() : 'unknown error';
      const userMessage = errorMessage.includes('not found')
        ? 'Legacy session link not found. Please request a new link.'
        : errorMessage.includes('expired')
          ? 'Legacy session link has expired. Please request a new link.'
          : 'Failed to load legacy session link. Please verify the URL or request a new one.';

      alert(userMessage);
    }
  };

  /**
   * Close the share modal
   */
  const closeShareModal = () => {
    setSessionState(prev => ({ ...prev, showShareModal: false }));
  };

  /**
   * Copy share URL to clipboard
   */
  const copyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(sessionState.shareUrl);
      return true;
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      return false;
    }
  };

  /**
   * Check if session loading was attempted (for useEffect dependency)
   */
  const markSessionLoadAttempted = () => {
    sessionLoadAttemptedRef.current = true;
  };

  const hasSessionLoadAttempted = () => {
    return sessionLoadAttemptedRef.current;
  };

  return {
    sessionState,
    setSessionState, // Expose for useLibrary hook
    handleLoadSessionFromUrl,
    closeShareModal,
    copyShareUrl,
    markSessionLoadAttempted,
    hasSessionLoadAttempted
  };
};
